import { Component, input, output, signal, computed, effect, forwardRef, model, ElementRef, ViewChild, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject, debounceTime } from 'rxjs';
import { TypeaheadItem } from '../../core/models/ui/typeahead-item.model';
import { PaginationConstants } from '../../core/constants/constants';
import { InViewPortDirective } from '../../core/directives/in-view-port.directive';
import { FaDuotoneIconComponent } from "@fortawesome/angular-fontawesome";

@Component({
  selector: 'ms-typeahead',
  standalone: true,
  imports: [CommonModule, FormsModule, FaDuotoneIconComponent, InViewPortDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => TypeaheadComponent),
      multi: true
    }
  ],
  templateUrl: 'typeahead.component.html',
  styleUrl: 'typeahead.component.scss'
})
export class TypeaheadComponent implements ControlValueAccessor {
  @ViewChild('searchInput') searchInput!: ElementRef<HTMLInputElement>;
  
  // input signals
  items = input<TypeaheadItem[]>([]);
  placeholder = input<string>('Search...');
  minLength = input<number>(1);
  noResultsText = input<string>('No results found');
  disabled = input<boolean>(false);
  invalid = input<boolean>(false);
  // The accessible name for the search field. A consumer cannot supply one itself: an aria-label or
  // a label's `for` placed on <ms-typeahead> lands on the custom host, not the native input inside
  // it, leaving the control unnamed to assistive technology.
  ariaLabel = input<string | undefined>(undefined);
  // When true, filtering happens server-side: the parent supplies already-filtered
  // items and receives (searchChanged) emissions to re-query. Client-side filtering
  // is bypassed. Defaults to false so existing consumers are unaffected.
  serverSideSearch = input<boolean>(false);
  isSearching = input<boolean>(false);
  // Infinite scroll (server-side mode only): hasMore signals another page exists;
  // isLoadingMore shows a footer spinner while the parent fetches it.
  hasMore = input<boolean>(false);
  isLoadingMore = input<boolean>(false);

  // model signal (two-way binding)
  selectedItem = model<TypeaheadItem | undefined>(undefined);

  // output signals
  cleared = output<void>();
  blurred = output<void>();
  searchChanged = output<string>();
  loadMore = output<void>();

  // internal signals
  searchTerm = signal<string>('');
  showDropdown = signal<boolean>(false);
  selectedIndex = signal<number>(-1);

  // The result panel is position:fixed so it escapes the overflow clipping of a table cell, a modal
  // or the sidebar — which also takes it out of flow, so a percentage width would resolve against the
  // viewport rather than the input. Measured from the rendered input instead, every time the panel
  // opens, so the two always line up whatever width the consumer gave the control.
  dropdownWidth = signal<number | undefined>(undefined);

  // Drives the panel's z-index band: a typeahead inside a modal has to paint above it, one outside
  // has to stay below it. Read from the DOM when the panel opens rather than in the constructor,
  // where the host is not reliably attached yet.
  isInsideModal = signal<boolean>(false);

  filteredItems = computed(() => {
    // In server-side mode the parent already filtered the list to match the term,
    // so surface it as-is rather than re-filtering client-side. Below minLength the
    // parent's list is stale (no query fired yet), so hide it to match client-side
    // gating — an empty term still shows the full list the parent supplied.
    if (this.serverSideSearch()) {
      const term = this.searchTerm();
      if (term.length > 0 && term.length < this.minLength()) {
        return [];
      }
      return this.items();
    }

    const term = this.searchTerm().toLowerCase();
    if (term.length === 0 && this.showDropdown()) {
      return this.items();
    }
    if (term.length < this.minLength()) {
      return [];
    }
    return this.items().filter(item =>
      item.label.toLowerCase().includes(term)
    );
  });

  private onChange: (value: any) => void = () => {};
  private onTouched: () => void = () => {};

  private destroyRef = inject(DestroyRef);
  private searchInput$ = new Subject<string>();
  private canLoadMore = true;

  constructor() {
    effect(() => {
      const term = this.searchTerm();
      if (term.length > 0) {
        this.selectedIndex.set(-1);
      }
    });

    // The typed term lives in this component, so a consumer that clears its own bound selection — a
    // Clear Filters button, or a form modal reopening to add rather than edit — has no other way to
    // empty the input. Selecting an item leaves selectedItem defined, so this does not fight typing.
    effect(() => {
      if (this.selectedItem() === undefined) {
        this.searchTerm.set('');
      }
    });

    // Debounce server-side search emissions so we re-query on a pause in typing
    // rather than on every keystroke. No distinctUntilChanged — its persistent
    // last-value would suppress a repeat search after the list is reloaded on reopen
    // (identical requests are already deduped by the caching interceptor anyway).
    this.searchInput$
      .pipe(
        debounceTime(PaginationConstants.SEARCH_DEBOUNCE_TIME_MILLISECONDS()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(term => this.searchChanged.emit(term));
  }

  public focusInput(): void {
    this.searchInput?.nativeElement?.focus();
  }

  public onLoadMore(): void {
    if (!this.serverSideSearch() || !this.hasMore() || !this.canLoadMore) {
      return;
    }

    // Latch so the sentinel re-entering view (as appended items push it down) doesn't
    // fire a burst of page requests. Re-armed after a debounce — mirrors BaseListPageClass.
    this.canLoadMore = false;
    this.loadMore.emit();
    setTimeout(() => {
      this.canLoadMore = true;
    }, PaginationConstants.LOAD_MORE_DEBOUNCE_TIME_MILLISECONDS());
  }

  clearSearch(): void {
    this.searchTerm.set('');
    this.openDropdown();

    // Focus moves to the clear button on click, so without this the input never blurs and the panel
    // it just reopened can no longer be dismissed by clicking away from it.
    this.searchInput?.nativeElement?.focus();

    this.onChange(null);
    this.cleared.emit();

    if (this.serverSideSearch()) {
      this.searchInput$.next('');
    }
  }

  onInputChange(): void {
    this.openDropdown();

    this.onChange(null);
    this.cleared.emit();

    // Only re-query once the term reaches minLength (an empty term is always allowed,
    // so clearing the field resets results). Avoids firing searches on sub-threshold
    // keystrokes for consumers that configure minLength > 1.
    if (this.serverSideSearch()) {
      const term = this.searchTerm();
      if (term.length === 0 || term.length >= this.minLength()) {
        this.searchInput$.next(term);
      }
    }
  }

  selectItem(item: TypeaheadItem): void {
    // Prevent selecting already selected item (avoids redundant API calls)
    if (this.selectedItem() && item.id === this.selectedItem()?.id) {
      return;
    }
    
    this.searchTerm.set(item.label);
    this.showDropdown.set(false);
    this.selectedIndex.set(this.items().indexOf(item));
    this.selectedItem.set(item);
    this.onChange(item);
  }

  onFocus(): void {
    this.openDropdown();
  }

  // The input is always rendered, so its width and its surroundings can be read the moment the panel
  // is asked to open — nothing has to wait for the panel itself to lay out.
  private openDropdown(): void {
    const input = this.searchInput?.nativeElement;

    this.dropdownWidth.set(input?.offsetWidth);
    this.isInsideModal.set(!!input?.closest('.modal'));
    this.showDropdown.set(true);
  }

  onBlur(): void {
    setTimeout(() => {
      this.showDropdown.set(false);
      this.selectedIndex.set(-1);
      this.onTouched();
      this.blurred.emit();
    }, 200);
  }

  onKeyDown(event: KeyboardEvent): void {
    if (!this.showDropdown() || this.filteredItems().length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.selectedIndex.update(index => 
          Math.min(index + 1, this.filteredItems().length - 1)
        );
        this.scrollToSelected();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.selectedIndex.update(index => Math.max(index - 1, 0));
        this.scrollToSelected();
        break;
      case 'Enter':
        event.preventDefault();
        const index = this.selectedIndex();
        if (index >= 0) {
          this.selectItem(this.filteredItems()[index]);
        }
        break;
      case 'Escape':
        this.showDropdown.set(false);
        this.selectedIndex.set(-1);
        break;
    }
  }

  private scrollToSelected(): void {
    const dropdown = document.querySelector('.dropdown-menu.show');
    const activeItem = dropdown?.querySelector('.dropdown-item.active');
    activeItem?.scrollIntoView({ block: 'nearest' });
  }

  writeValue(value: any): void {
    if (value && typeof value === 'object' && value.label) {
      this.searchTerm.set(value.label);
    } else if (typeof value === 'string') {
      this.searchTerm.set(value);
    } else {
      this.searchTerm.set('');
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }
}
